import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Building2, GraduationCap, Briefcase, Landmark, Download, Sparkles, RotateCw, ChevronLeft, ChevronRight, Loader2, Waves, Dumbbell, Home, Theater, Building, Baby, TrainFront, Hotel, Store, Camera, Upload, X } from 'lucide-react';
import { API_URL } from '../config';
import { NewBadge, isNew } from './NewBadge';



const roomTypes = [
  { id: 'hospital', icon: Building2 },
  { id: 'school', icon: GraduationCap },
  { id: 'kindergarten', icon: Baby },
  { id: 'office', icon: Briefcase },
  { id: 'commercial', icon: Store },
  { id: 'admin', icon: Landmark },
  { id: 'residential', icon: Building },
  { id: 'hotel', icon: Hotel },
  { id: 'pool', icon: Waves },
  { id: 'gym', icon: Dumbbell },
  { id: 'dormitory', icon: Home },
  { id: 'auditorium', icon: Theater },
  { id: 'station', icon: TrainFront },
];

const roomSubTypes = {
  hospital:    ['cabinet', 'corridor', 'lobby', 'reception'],
  school:      ['classroom', 'corridor', 'lobby', 'gym_hall'],
  kindergarten:['playroom', 'corridor', 'lobby', 'bedroom'],
  office:      ['cabinet', 'corridor', 'lobby', 'meeting'],
  commercial:  ['showroom', 'corridor', 'lobby', 'reception', 'common'],
  admin:       ['cabinet', 'corridor', 'lobby', 'reception'],
  residential: ['ext_corridor', 'lobby'],
  hotel:       ['room', 'corridor', 'lobby', 'reception'],
  pool:        ['pool_area', 'corridor', 'lobby', 'changing'],
  gym:         ['gym_hall', 'corridor', 'lobby', 'changing'],
  dormitory:   ['room', 'corridor', 'lobby', 'common'],
  auditorium:  ['hall', 'corridor', 'lobby', 'foyer'],
  station:     ['hall', 'corridor', 'lobby'],
};

const subTypeLabels = {
  ru: { room: 'Комната', cabinet: 'Кабинет', corridor: 'Коридор', lobby: 'Лобби', reception: 'Ресепшн', classroom: 'Учебный класс', gym_hall: 'Спортзал', meeting: 'Переговорная', pool_area: 'Зона бассейна', changing: 'Раздевалка', common: 'Общая зона', hall: 'Зал', foyer: 'Фойе', ext_corridor: 'Внеквартирный коридор', playroom: 'Игровая', bedroom: 'Спальня', platform: 'Перрон/платформа', showroom: 'Шоурум/Торговый зал' },
  kz: { room: 'Бөлме', cabinet: 'Кабинет', corridor: 'Дәліз', lobby: 'Лобби', reception: 'Ресепшн', classroom: 'Оқу сыныбы', gym_hall: 'Спорт залы', meeting: 'Кеңес бөлмесі', pool_area: 'Бассейн аймағы', changing: 'Киім ауыстыру', common: 'Ортақ аймақ', hall: 'Зал', foyer: 'Фойе', ext_corridor: 'Пәтерден тыс дәліз', playroom: 'Ойын бөлмесі', bedroom: 'Ұйықтау бөлмесі', platform: 'Платформа', showroom: 'Шоурум' },
  en: { room: 'Room', cabinet: 'Office/Cabinet', corridor: 'Corridor', lobby: 'Lobby', reception: 'Reception', classroom: 'Classroom', gym_hall: 'Gym Hall', meeting: 'Meeting Room', pool_area: 'Pool Area', changing: 'Changing Room', common: 'Common Area', hall: 'Hall', foyer: 'Foyer', ext_corridor: 'Common Corridor', playroom: 'Playroom', bedroom: 'Bedroom', platform: 'Platform', showroom: 'Showroom' },
  ar: { room: 'غرفة', cabinet: 'مكتب/غرفة عمل', corridor: 'ممر', lobby: 'بهو', reception: 'استقبال', classroom: 'فصل دراسي', gym_hall: 'قاعة رياضية', meeting: 'قاعة اجتماعات', pool_area: 'منطقة المسبح', changing: 'غرفة تغيير الملابس', common: 'منطقة مشتركة', hall: 'قاعة', foyer: 'ردهة', ext_corridor: 'ممر مشترك', playroom: 'غرفة ألعاب', bedroom: 'غرفة نوم', platform: 'رصيف', showroom: 'صالة عرض' },
};

const roomLabels = {
  ru: { hospital: 'Больница', school: 'Школа', kindergarten: 'Детский сад', office: 'Офис', commercial: 'Коммерческое помещение', admin: 'Админ. здание', residential: 'Жилой комплекс', hotel: 'Гостиница', pool: 'Бассейн', gym: 'Спорткомплекс', dormitory: 'Общежитие', auditorium: 'Актовый зал', station: 'Вокзал' },
  kz: { hospital: 'Аурухана', school: 'Мектеп', kindergarten: 'Балабақша', office: 'Кеңсе', commercial: 'Коммерциялық үй-жай', admin: 'Әкімшілік ғимарат', residential: 'Тұрғын үй', hotel: 'Қонақ үй', pool: 'Бассейн', gym: 'Спорт кешені', dormitory: 'Жатақхана', auditorium: 'Мәжіліс залы', station: 'Вокзал' },
  en: { hospital: 'Hospital', school: 'School', kindergarten: 'Kindergarten', office: 'Office', commercial: 'Commercial Space', admin: 'Admin Building', residential: 'Residential', hotel: 'Hotel', pool: 'Pool', gym: 'Sports Complex', dormitory: 'Dormitory', auditorium: 'Auditorium', station: 'Station' },
  ar: { hospital: 'مستشفى', school: 'مدرسة', kindergarten: 'روضة أطفال', office: 'مكتب', commercial: 'فضاء تجاري', admin: 'مبنى إداري', residential: 'سكني', hotel: 'فندق', pool: 'مسبح', gym: 'مجمع رياضي', dormitory: 'سكن داخلي', auditorium: 'قاعة مؤتمرات', station: 'محطة' }
};

const uiText = {
  ru: {
    title: 'AI-Конструктор интерьеров',
    subtitle: 'Визуализируйте панели GIPSOVINIL.KZ в реальных помещениях',
    step1: 'Выберите помещение',
    step2: 'Выберите панель',
    step3: 'Результат',
    orientation: 'Ориентация панелей',
    vertical: 'Вертикально',
    horizontal: 'Горизонтально',
    panelWidth: 'Ширина панели',
    ceilingHeight: 'Высота потолка',
    panelLayout: 'Раскладка панелей',
    generate: 'Сгенерировать визуализацию',
    generating: 'Генерация изображения... (~30 сек)',
    download: 'Скачать изображение',
    regenerate: 'Сгенерировать заново',
    selectRoom: 'Выберите тип помещения',
    selectPanel: 'Выберите панель из каталога',
    noResult: 'Выберите помещение и панель, затем нажмите "Сгенерировать"',
    error: 'Ошибка генерации. Попробуйте ещё раз.',
    allCollections: 'Все',
    panelSearch: 'Поиск панели...',
    modeFromScratch: 'ИИ дизайн',
    modeFromPhoto: 'По моему фото',
    uploadPhoto: 'Загрузите фото помещения',
    uploadPhotoHint: 'JPG/PNG до 12 МБ',
    photoSelected: 'Фото загружено',
    changePhoto: 'Заменить фото',
    selectPhotoFirst: 'Сначала загрузите фото помещения'
  },
  kz: {
    title: 'AI-Интерьер конструкторы',
    subtitle: 'GIPSOVINIL.KZ панельдерін нақты бөлмелерде визуализациялаңыз',
    step1: 'Бөлмені таңдаңыз',
    step2: 'Панельді таңдаңыз',
    step3: 'Нәтиже',
    orientation: 'Панель бағыты',
    vertical: 'Тігінен',
    horizontal: 'Көлденеңінен',
    panelWidth: 'Панель ені',
    ceilingHeight: 'Төбе биіктігі',
    panelLayout: 'Панель орналасуы',
    generate: 'Визуализация жасау',
    generating: 'Сурет жасалуда... (~30 сек)',
    download: 'Суретті жүктеу',
    regenerate: 'Қайта жасау',
    selectRoom: 'Бөлме түрін таңдаңыз',
    selectPanel: 'Каталогтан панельді таңдаңыз',
    noResult: 'Бөлме мен панельді таңдап, "Жасау" батырмасын басыңыз',
    error: 'Жасау қатесі. Қайталап көріңіз.',
    allCollections: 'Барлығы',
    panelSearch: 'Панель іздеу...',
    modeFromScratch: 'ИИ дизайн',
    modeFromPhoto: 'Менің фотом бойынша',
    uploadPhoto: 'Бөлме фотосын жүктеңіз',
    uploadPhotoHint: 'JPG/PNG 12 МБ дейін',
    photoSelected: 'Фото жүктелді',
    changePhoto: 'Фотоны ауыстыру',
    selectPhotoFirst: 'Алдымен бөлме фотосын жүктеңіз'
  },
  en: {
    title: 'AI Interior Constructor',
    subtitle: 'Visualize GIPSOVINIL.KZ panels in real spaces',
    step1: 'Choose Room',
    step2: 'Choose Panel',
    step3: 'Result',
    orientation: 'Panel Orientation',
    vertical: 'Vertical',
    horizontal: 'Horizontal',
    panelWidth: 'Panel Width',
    ceilingHeight: 'Ceiling Height',
    panelLayout: 'Panel Layout',
    generate: 'Generate Visualization',
    generating: 'Generating image... (~30 sec)',
    download: 'Download Image',
    regenerate: 'Regenerate',
    selectRoom: 'Select room type',
    selectPanel: 'Select a panel from catalog',
    noResult: 'Select room and panel, then click "Generate"',
    error: 'Generation error. Please try again.',
    allCollections: 'All',
    panelSearch: 'Search panel...',
    modeFromScratch: 'ИИ дизайн',
    modeFromPhoto: 'From My Photo',
    uploadPhoto: 'Upload room photo',
    uploadPhotoHint: 'JPG/PNG up to 12 MB',
    photoSelected: 'Photo uploaded',
    changePhoto: 'Replace photo',
    selectPhotoFirst: 'Please upload a room photo first'
  },
  ar: {
    title: 'منشئ التصميم الداخلي بالذكاء الاصطناعي',
    subtitle: 'تصوّر ألواح GIPSOVINIL.KZ في الفضاءات الحقيقية',
    step1: 'اختر الغرفة',
    step2: 'اختر اللوح',
    step3: 'النتيجة',
    orientation: 'اتجاه اللوح',
    vertical: 'عمودي',
    horizontal: 'أفقي',
    panelWidth: 'عرض اللوح',
    ceilingHeight: 'ارتفاع السقف',
    panelLayout: 'تخطيط الألواح',
    generate: 'توليد التصور المرئي',
    generating: 'جارٍ توليد الصورة... (~30 ثانية)',
    download: 'تحميل الصورة',
    regenerate: 'إعادة التوليد',
    selectRoom: 'اختر نوع الغرفة',
    selectPanel: 'اختر لوحاً من الكتالوج',
    noResult: 'اختر الغرفة واللوح، ثم اضغط "توليد"',
    error: 'خطأ في التوليد. يرجى المحاولة مجدداً.',
    allCollections: 'الكل',
    panelSearch: 'البحث عن لوح...',
    modeFromScratch: 'تصميم بالذكاء الاصطناعي',
    modeFromPhoto: 'من صورتي',
    uploadPhoto: 'رفع صورة الغرفة',
    uploadPhotoHint: 'JPG/PNG حتى 12 ميغابايت',
    photoSelected: 'تم رفع الصورة',
    changePhoto: 'استبدال الصورة',
    selectPhotoFirst: 'يرجى رفع صورة الغرفة أولاً'
  }
};

const InteriorConstructor = ({ catalogData, exclusiveCollections, catalogImages }) => {
  const { language } = useLanguage();
  const t = uiText[language] || uiText.ru;
  const labels = roomLabels[language] || roomLabels.ru;

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedSubType, setSelectedSubType] = useState(null);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [secondPanel, setSecondPanel] = useState(null);
  const [comboMode, setComboMode] = useState(false);
  const [orientation, setOrientation] = useState('vertical');
  const [ceilingHeight, setCeilingHeight] = useState(3.0);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);
  const [panelFilter, setPanelFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectingSecond, setSelectingSecond] = useState(false);
  const [profileColor, setProfileColor] = useState('match');
  const [userComment, setUserComment] = useState('');
  const [imageFormat, setImageFormat] = useState('landscape');
  const [generationMode, setGenerationMode] = useState('scratch'); // 'scratch' | 'photo'
  const [roomPhotoFile, setRoomPhotoFile] = useState(null);
  const [roomPhotoPreview, setRoomPhotoPreview] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [elapsed, setElapsed] = useState(0); // seconds since current job started (UI progress)
  const photoInputRef = useRef(null);
  const panelScrollRef = useRef(null);
  const pollAbortRef = useRef(null); // {jobId, intervalId, elapsedIntervalId, timeoutId} for cleanup

  // Calculate panel layout based on ceiling height
  const calcLayout = (heightM, orient) => {
    const ceilingMm = Math.round(heightM * 1000);
    if (orient === 'horizontal') {
      const rows = [];
      let remaining = ceilingMm;
      while (remaining > 0) {
        if (remaining >= 1200) { rows.push(1200); remaining -= 1200; }
        else { rows.push(remaining); remaining = 0; }
      }
      return rows;
    }
    const available = [3000, 2700, 2500];
    const rows = [];
    let remaining = ceilingMm;
    while (remaining > 0) {
      let chosen = null;
      for (const h of available) {
        if (h <= remaining) { chosen = h; break; }
      }
      if (!chosen) chosen = Math.min(remaining, 2500);
      rows.push(chosen);
      remaining -= chosen;
    }
    return rows;
  };

  const panelLayout = calcLayout(ceilingHeight, orientation);

  // Build flat panel list from catalog data
  const allPanels = [];
  if (catalogData) {
    Object.entries(catalogData).forEach(([cat, items]) => {
      items.forEach(item => allPanels.push({ ...item, category: cat }));
    });
  }
  if (exclusiveCollections?.oriental?.textures) {
    exclusiveCollections.oriental.textures.forEach(item => allPanels.push({ ...item, category: 'oriental' }));
  }
  if (exclusiveCollections?.grey?.textures) {
    exclusiveCollections.grey.textures.forEach(item => allPanels.push({ ...item, category: 'grey' }));
  }
  if (exclusiveCollections?.nomad?.ornaments && exclusiveCollections?.nomad?.colors) {
    // Nomad excluded from constructor — only in catalog
  }

  const catLabels = {
    ru: { all: 'Все', oriental: 'Восточные вкусы', grey: 'Серые', standard: 'Стандарт', cashmere: 'Кашемир', premium: 'Премиум', superPremium: 'Супер-Премиум', luxury: 'Люкс' },
    kz: { all: 'Барлығы', oriental: 'Шығыс дәмі', grey: 'Сұр', standard: 'Стандарт', cashmere: 'Кашемир', premium: 'Премиум', superPremium: 'Супер-Премиум', luxury: 'Люкс' },
    en: { all: 'All', oriental: 'Oriental Flavour', grey: 'Grey', standard: 'Standard', cashmere: 'Cashmere', premium: 'Premium', superPremium: 'Super-Premium', luxury: 'Luxury' },
    ar: { all: 'الكل', oriental: 'النكهة الشرقية', grey: 'رمادي', standard: 'قياسي', cashmere: 'كاشمير', premium: 'بريميوم', superPremium: 'سوبر بريميوم', luxury: 'فاخر' }
  };
  const cl = catLabels[language] || catLabels.ru;

  const filteredPanels = allPanels.filter(p => {
    const matchesCat = panelFilter === 'all' || p.category === panelFilter;
    const matchesSearch = !searchQuery || p.name[language]?.toLowerCase().includes(searchQuery.toLowerCase()) || p.code?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  // Get texture image URL for a panel
  const getTextureUrl = (panel) => {
    if (!panel) return null;
    if (catalogImages && catalogImages[`${panel.category}_${panel.code}`]) {
      return catalogImages[`${panel.category}_${panel.code}`];
    }
    return panel.image || null;
  };

  const startCooldown = (seconds) => {
    setCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // Cleanup polling and cooldown timers on unmount.
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      if (pollAbortRef.current) {
        const { intervalId, elapsedIntervalId, timeoutId } = pollAbortRef.current;
        if (intervalId) clearInterval(intervalId);
        if (elapsedIntervalId) clearInterval(elapsedIntervalId);
        if (timeoutId) clearTimeout(timeoutId);
        pollAbortRef.current = null;
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollAbortRef.current) {
      const { intervalId, elapsedIntervalId, timeoutId } = pollAbortRef.current;
      if (intervalId) clearInterval(intervalId);
      if (elapsedIntervalId) clearInterval(elapsedIntervalId);
      if (timeoutId) clearTimeout(timeoutId);
      pollAbortRef.current = null;
    }
  };

  const pollJobUntilDone = (jobId, dealerToken) => new Promise((resolve, reject) => {
    const startTs = Date.now();
    const MAX_WAIT_MS = 180_000; // 3 min hard cap on polling
    const POLL_EVERY_MS = 2500;

    const elapsedIntervalId = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTs) / 1000));
    }, 1000);

    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/api/constructor/jobs/${jobId}`, {
          headers: dealerToken ? { Authorization: `Bearer ${dealerToken}` } : {},
        });
        if (!r.ok) {
          if (r.status === 404 || r.status === 403) {
            stopPolling();
            reject(new Error(`status ${r.status}`));
            return;
          }
          return; // transient — keep polling
        }
        const j = await r.json();
        if (j.status === 'completed') {
          stopPolling();
          resolve(j);
        } else if (j.status === 'failed') {
          stopPolling();
          const err = new Error(j.error || 'Job failed');
          err.statusCode = j.error_status_code;
          reject(err);
        }
      } catch (_) {
        // network glitch — let the next tick try again
      }
    };

    const intervalId = setInterval(tick, POLL_EVERY_MS);
    const timeoutId = setTimeout(() => {
      stopPolling();
      reject(new Error('Polling timed out (>3 min)'));
    }, MAX_WAIT_MS);

    pollAbortRef.current = { jobId, intervalId, elapsedIntervalId, timeoutId };
    // Kick off the first poll quickly so users see status without waiting 2.5s
    setTimeout(tick, 500);
  });

  const handleGenerate = async () => {
    if (!selectedPanel || cooldown > 0) return;
    if (generationMode === 'scratch' && !selectedRoom) return;
    if (generationMode === 'photo' && !roomPhotoFile) {
      setError(t.selectPhotoFirst);
      return;
    }
    setLoading(true);
    setError(null);
    setGeneratedImage(null);
    setAttemptCount((c) => c + 1);
    setElapsed(0);
    stopPolling(); // safety: cancel any previous poll

    const textureUrl = getTextureUrl(selectedPanel);
    const secondTextureUrl = secondPanel ? getTextureUrl(secondPanel) : null;
    const dealerToken = localStorage.getItem('dealerToken') || '';

    try {
      let createRes;
      if (generationMode === 'photo') {
        const fd = new FormData();
        fd.append('photo', roomPhotoFile);
        fd.append('panel_color', selectedPanel.color);
        fd.append('panel_name', selectedPanel.name[language] || selectedPanel.name.ru);
        if (textureUrl) fd.append('texture_image_url', `${API_URL}${textureUrl}`);
        fd.append('profile_color', profileColor);
        fd.append('orientation', orientation);
        fd.append('ceiling_height', String(ceilingHeight));
        if (userComment) fd.append('user_comment', userComment);
        if (comboMode && secondPanel) {
          fd.append('second_panel_color', secondPanel.color);
          fd.append('second_panel_name', secondPanel.name[language] || secondPanel.name.ru);
          if (secondTextureUrl) fd.append('second_texture_image_url', `${API_URL}${secondTextureUrl}`);
        }
        createRes = await fetch(`${API_URL}/api/constructor/jobs/from-photo`, {
          method: 'POST',
          headers: dealerToken ? { Authorization: `Bearer ${dealerToken}` } : {},
          body: fd,
        });
      } else {
        const body = {
          room_type: selectedRoom,
          room_sub_type: selectedSubType || null,
          panel_color: selectedPanel.color,
          panel_name: selectedPanel.name[language] || selectedPanel.name.ru,
          orientation,
          ceiling_height: ceilingHeight,
          texture_image_url: textureUrl ? `${API_URL}${textureUrl}` : null,
          profile_color: profileColor,
          user_comment: userComment || null,
          image_format: imageFormat,
        };
        if (comboMode && secondPanel) {
          body.second_panel_color = secondPanel.color;
          body.second_panel_name = secondPanel.name[language] || secondPanel.name.ru;
          body.second_texture_image_url = secondTextureUrl ? `${API_URL}${secondTextureUrl}` : null;
        }
        createRes = await fetch(`${API_URL}/api/constructor/jobs/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(dealerToken ? { Authorization: `Bearer ${dealerToken}` } : {}),
          },
          body: JSON.stringify(body),
        });
      }

      if (!createRes.ok) {
        const errData = await createRes.json().catch(() => ({}));
        const detail = String(errData.detail || createRes.statusText || `HTTP ${createRes.status}`);
        if (createRes.status === 401 || createRes.status === 403) {
          setError(language === 'ru' ? 'Войдите в кабинет дилера для генерации.' : 'Login required to generate.');
        } else {
          setError(`${t.error}: ${detail.length > 220 ? detail.slice(0, 220) + '…' : detail}`);
        }
        return;
      }

      const createJson = await createRes.json();
      const jobId = createJson.job_id;
      if (!jobId) {
        setError(`${t.error}: no job_id returned`);
        return;
      }
      // Track ownership locally to avoid cross-account peek if user switches accounts.
      try {
        localStorage.setItem(`constructorJob:${jobId}`, JSON.stringify({ created_at: Date.now() }));
      } catch (_) { /* ignore quota */ }

      const job = await pollJobUntilDone(jobId, dealerToken);
      if (job?.image_url) {
        // image_url is a relative path like /api/static/constructor/<uuid>.jpg
        const absUrl = job.image_url.startsWith('http') ? job.image_url : `${API_URL}${job.image_url}`;
        setGeneratedImage(absUrl);
        setAttemptCount(0);
        startCooldown(15);
      } else {
        setError(`${t.error}: job completed but no image_url`);
      }
    } catch (e) {
      const sc = e?.statusCode;
      const msg = e?.message || '';
      if (sc === 504 || /timeout|taking longer/i.test(msg)) {
        setError(language === 'ru' ? 'AI занят и не успел ответить. Попробуйте ещё раз — вторая попытка обычно проходит за 10–15 секунд.' : 'AI is busy. Try again — second attempt usually completes in 10–15 s.');
        startCooldown(30);
      } else if (sc === 503 || /overload|429|quota/i.test(msg)) {
        setError(language === 'ru' ? 'AI-сервис временно перегружен. Подождите минуту и попробуйте снова.' : 'AI service temporarily overloaded. Wait a minute and try again.');
        startCooldown(60);
      } else if (/polling timed out/i.test(msg)) {
        setError(language === 'ru' ? 'Генерация заняла больше 3 минут. Попробуйте ещё раз.' : 'Generation took longer than 3 min. Please try again.');
      } else {
        setError(msg ? `${t.error}: ${msg}` : t.error);
      }
    } finally {
      stopPolling();
      setLoading(false);
    }
  };

  const handlePhotoSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      setError(language === 'ru' ? 'Размер фото больше 12 МБ' : 'Photo larger than 12 MB');
      return;
    }
    setRoomPhotoFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setRoomPhotoPreview(ev.target.result);
    reader.readAsDataURL(f);
    setError(null);
  };

  const clearPhoto = () => {
    setRoomPhotoFile(null);
    setRoomPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `gipsovinil_${selectedRoom}_${selectedPanel?.code || 'panel'}.png`;
    link.click();
  };

  const scrollPanels = (dir) => {
    if (panelScrollRef.current) {
      panelScrollRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-8 sm:py-16 bg-[#061d36]" data-testid="interior-constructor">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8b4444]/20 rounded-full mb-4">
            <Sparkles className="w-5 h-5 text-[#8b4444]" />
            <span className="text-[#8b4444] font-medium text-sm uppercase tracking-wider">AI</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-testid="constructor-title">{t.title}</h2>
          <p className="text-white/60 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Controls */}
          <div className="space-y-8 min-w-0">
            {/* Mode switch */}
            <div className="flex bg-white/5 rounded-xl p-1 border border-white/10" data-testid="constructor-mode-switch">
              <button
                onClick={() => setGenerationMode('scratch')}
                className={`flex-1 py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${generationMode === 'scratch' ? 'bg-[#8b4444] text-white' : 'text-white/60 hover:text-white'}`}
                data-testid="mode-scratch"
              >
                <Sparkles className="w-4 h-4 flex-shrink-0" /> {t.modeFromScratch}
              </button>
              <button
                onClick={() => setGenerationMode('photo')}
                className={`flex-1 py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${generationMode === 'photo' ? 'bg-[#8b4444] text-white' : 'text-white/60 hover:text-white'}`}
                data-testid="mode-photo"
              >
                <Camera className="w-4 h-4 flex-shrink-0" /> {t.modeFromPhoto}
              </button>
            </div>

            {/* Step 1: Room Type (scratch mode) OR Photo Upload (photo mode) */}
            {generationMode === 'photo' ? (
              <div>
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 bg-[#8b4444] rounded-lg flex items-center justify-center text-white text-sm font-bold">1</span>
                  {t.uploadPhoto}
                </h3>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelected}
                  className="hidden"
                  data-testid="photo-upload-input"
                />
                {!roomPhotoPreview ? (
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 hover:border-[#8b4444] hover:bg-white/5 transition-all flex flex-col items-center gap-3 text-white/70 hover:text-white"
                    data-testid="photo-upload-button"
                  >
                    <Upload className="w-12 h-12" />
                    <div className="text-base font-semibold">{t.uploadPhoto}</div>
                    <div className="text-xs text-white/50">{t.uploadPhotoHint}</div>
                  </button>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-white/10" data-testid="photo-preview">
                    <img src={roomPhotoPreview} alt="Room" className="w-full h-auto max-h-80 object-cover" />
                    <button
                      onClick={clearPhoto}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/70 hover:bg-black text-white rounded-full flex items-center justify-center"
                      data-testid="photo-clear-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      className="absolute bottom-2 right-2 px-3 py-1.5 bg-black/70 hover:bg-black text-white text-xs rounded-full"
                    >
                      {t.changePhoto}
                    </button>
                  </div>
                )}
              </div>
            ) : (
            <div>
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-7 h-7 bg-[#8b4444] rounded-lg flex items-center justify-center text-white text-sm font-bold">1</span>
                {t.step1}
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {roomTypes.map(room => {
                  const Icon = room.icon;
                  const isSelected = selectedRoom === room.id;
                  return (
                    <button
                      key={room.id}
                      onClick={() => { setSelectedRoom(room.id); setSelectedSubType(null); }}
                      className={`p-2 sm:p-4 rounded-xl border-2 transition-all text-center ${
                        isSelected
                          ? 'border-[#8b4444] bg-[#8b4444]/20 text-white'
                          : 'border-white/10 bg-white/5 text-white/60 hover:border-white/30'
                      }`}
                      data-testid={`room-${room.id}`}
                    >
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2" />
                      <span className="text-[10px] sm:text-xs font-medium leading-tight break-words block">{labels[room.id]}</span>
                    </button>
                  );
                })}
              </div>

              {/* Sub-type selection */}
              {selectedRoom && roomSubTypes[selectedRoom] && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {roomSubTypes[selectedRoom].map(sub => {
                    const subLabels = subTypeLabels[language] || subTypeLabels.ru;
                    const isActive = selectedSubType === sub;
                    return (
                      <button key={sub} onClick={() => setSelectedSubType(isActive ? null : sub)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${isActive ? 'bg-[#8b4444] text-white border-[#8b4444]' : 'bg-white/5 text-white/50 border-white/10 hover:border-white/30 hover:text-white/80'}`}
                        data-testid={`sub-${sub}`}>
                        {subLabels[sub] || sub}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Step 2: Panel Selection */}
            <div>
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-7 h-7 bg-[#8b4444] rounded-lg flex items-center justify-center text-white text-sm font-bold">2</span>
                {t.step2}
              </h3>

              {/* Category filter */}
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.keys(cl).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setPanelFilter(cat)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      panelFilter === cat
                        ? 'bg-[#8b4444] text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                    }`}
                  >
                    {cl[cat]}
                  </button>
                ))}
              </div>

              {/* Search */}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.panelSearch}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/30 mb-3 focus:outline-none focus:border-[#8b4444]"
                data-testid="panel-search"
              />

              {/* Panel grid with scroll */}
              <div className="relative">
                <button onClick={() => scrollPanels(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-[#061d36]/90 rounded-full flex items-center justify-center text-white hover:bg-[#061d36]">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div ref={panelScrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide py-2 px-8" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                  {filteredPanels.map((panel, idx) => {
                    const isSelected = selectedPanel?.code === panel.code;
                    const isSecond = secondPanel?.code === panel.code;
                    const texUrl = getTextureUrl(panel);
                    return (
                      <button
                        key={`${panel.code}-${idx}`}
                        onClick={() => {
                          if (selectingSecond) {
                            setSecondPanel(panel);
                            setSelectingSecond(false);
                          } else {
                            setSelectedPanel(panel);
                          }
                        }}
                        className={`flex-shrink-0 w-24 rounded-xl overflow-hidden border-2 transition-all ${
                          isSelected ? 'border-[#8b4444] scale-105' : isSecond ? 'border-amber-400 scale-105' : 'border-white/10 hover:border-white/30'
                        }`}
                        data-testid={`constructor-panel-${panel.code}`}
                      >
                        <div className="w-24 h-24 relative" style={{ backgroundColor: panel.color }}>
                          {texUrl && <img src={texUrl} alt="" className="absolute inset-0 w-full h-full object-cover object-center" loading="lazy" />}
                          {isNew(panel.newUntil) && <NewBadge className="absolute top-1 left-1 z-10" size="sm" />}
                        </div>
                        <div className="p-1.5 bg-white/5">
                          <p className="text-white text-[10px] font-medium truncate">{panel.name[language]}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => scrollPanels(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-[#061d36]/90 rounded-full flex items-center justify-center text-white hover:bg-[#061d36]">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Selected panel info */}
              {selectedPanel && (
                <div className="mt-3 flex items-center gap-3 bg-white/5 rounded-xl p-3 border border-[#8b4444]/30">
                  <div className="w-10 h-10 rounded-lg flex-shrink-0 relative overflow-hidden" style={{ backgroundColor: selectedPanel.color }}>
                    {getTextureUrl(selectedPanel) && <img src={getTextureUrl(selectedPanel)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{selectedPanel.name[language]}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-white/40 text-xs">APT: {selectedPanel.code}</p>
                      {isNew(selectedPanel.newUntil) && <NewBadge size="sm" />}
                    </div>
                  </div>
                  {getTextureUrl(selectedPanel) && (
                    <span className="text-[10px] text-green-400 bg-green-400/10 px-2 py-1 rounded-lg">
                      {language === 'ru' ? 'текстура' : language === 'kz' ? 'текстура' : 'texture'}
                    </span>
                  )}
                </div>
              )}

              {/* Combo mode toggle (both scratch and photo modes) */}
              <div className="mt-3">
                <button
                  onClick={() => { setComboMode(p => !p); setSecondPanel(null); setSelectingSecond(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                    comboMode ? 'border-amber-400 bg-amber-400/10 text-amber-400' : 'border-white/10 text-white/40 hover:border-white/30'
                  }`}
                  data-testid="combo-mode-toggle"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" /></svg>
                  {language === 'en' ? 'Combine 2 colors' : language === 'kz' ? '2 түсті біріктіру' : 'Комбинация 2 цвета'}
                </button>
              </div>

              {/* Second panel selector (combo mode) */}
              {comboMode && (
                <div className="mt-2">
                  {secondPanel ? (
                    <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3 border border-amber-400/30">
                      <div className="w-10 h-10 rounded-lg flex-shrink-0 relative overflow-hidden" style={{ backgroundColor: secondPanel.color }}>
                        {getTextureUrl(secondPanel) && <img src={getTextureUrl(secondPanel)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{secondPanel.name[language]}</p>
                        <p className="text-white/40 text-xs">APT: {secondPanel.code}</p>
                      </div>
                      <button onClick={() => { setSecondPanel(null); setSelectingSecond(true); }} className="text-xs text-amber-400 hover:underline">
                        {language === 'en' ? 'Change' : 'Изменить'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSelectingSecond(true)}
                      className={`w-full py-3 rounded-xl border-2 border-dashed transition-all text-sm ${
                        selectingSecond ? 'border-amber-400 text-amber-400 bg-amber-400/5' : 'border-white/20 text-white/40 hover:border-white/40'
                      }`}
                      data-testid="select-second-panel"
                    >
                      {selectingSecond
                        ? (language === 'en' ? 'Now click a panel above' : 'Выберите панель из каталога выше')
                        : (language === 'en' ? '+ Select second color' : '+ Выбрать второй цвет')
                      }
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Orientation (both modes) */}
            <div>
              <h3 className="text-white/60 font-medium text-sm mb-3">{t.orientation}</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setOrientation('vertical')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all border-2 ${
                    orientation === 'vertical'
                      ? 'border-[#8b4444] bg-[#8b4444]/20 text-white'
                      : 'border-white/10 text-white/50 hover:border-white/30'
                  }`}
                >
                  {t.vertical}
                </button>
                <button
                  onClick={() => setOrientation('horizontal')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all border-2 ${
                    orientation === 'horizontal'
                      ? 'border-[#8b4444] bg-[#8b4444]/20 text-white'
                      : 'border-white/10 text-white/50 hover:border-white/30'
                  }`}
                >
                  {t.horizontal}
                </button>
              </div>
            </div>

            {/* Profile Color */}
            <div>
              <h3 className="text-white/60 font-medium text-sm mb-3">{language === 'en' ? 'Joint profile color' : language === 'kz' ? 'Тігіс профиль түсі' : 'Цвет стыковочного профиля'}</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'match', label: language === 'en' ? 'Match panel' : 'В цвет панели', color: selectedPanel?.color || '#888', border: 'border-white/30' },
                  { key: 'black', label: language === 'en' ? 'Black' : 'Черный', color: '#1a1a1a', border: 'border-white/20' },
                  { key: 'white', label: language === 'en' ? 'White' : 'Белый', color: '#ffffff', border: 'border-white/20' },
                  { key: 'gold', label: language === 'en' ? 'Gold' : 'Золото', color: '#d4af37', border: 'border-white/20' },
                  { key: 'brass', label: language === 'en' ? 'Brass' : 'Латунь', color: '#b5a642', border: 'border-white/20' },
                  { key: 'led', label: language === 'en' ? 'LED strip' : 'Светодиод', color: '#00e5ff', border: 'border-white/20' },
                  { key: 'ral9002', label: 'RAL 9002', color: '#d7d5cb', border: 'border-white/20' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setProfileColor(opt.key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all border-2 ${
                      profileColor === opt.key
                        ? 'border-[#8b4444] bg-[#8b4444]/20 text-white'
                        : `${opt.border} text-white/50 hover:border-white/30`
                    }`}
                    data-testid={`profile-color-${opt.key}`}
                  >
                    <span className="w-4 h-4 rounded-full border border-white/20 flex-shrink-0" style={{ backgroundColor: opt.color }} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Ceiling Height + Panel Layout (both modes) */}
            <div>
              <h3 className="text-white/60 font-medium text-sm mb-3">{t.ceilingHeight}</h3>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <input
                  type="number"
                  min="2.5"
                  max="6"
                  step="0.1"
                  value={ceilingHeight}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (v >= 2.5 && v <= 6) setCeilingHeight(v);
                  }}
                  className="w-24 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm text-center focus:outline-none focus:border-[#8b4444]"
                  data-testid="ceiling-height-input"
                />
                <span className="text-white/40 text-sm">м</span>
                <div className="flex flex-wrap gap-2 sm:ml-auto">
                  {[2.5, 2.7, 3.0, 3.5, 4.5].map(h => (
                    <button
                      key={h}
                      onClick={() => setCeilingHeight(h)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        ceilingHeight === h
                          ? 'bg-[#8b4444] text-white'
                          : 'bg-white/10 text-white/50 hover:bg-white/20'
                      }`}
                    >
                      {h}м
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <p className="text-white/40 text-xs mb-2">{t.panelLayout}:</p>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-white/60 text-[10px]">1200 мм</span>
                    {panelLayout.map((h, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <div
                          className="bg-[#8b4444]/30 border border-[#8b4444]/60 rounded"
                          style={{ width: '40px', height: `${Math.max(h / 50, 20)}px` }}
                        />
                        <span className="text-white/40 text-[10px]">{h} мм</span>
                      </div>
                    ))}
                  </div>
                </div>
                {panelLayout.length > 1 && (
                  <p className="text-white/50 text-[10px] mt-2">
                    {language === 'ru' ? `${panelLayout.length} ряда панелей: ${panelLayout.map(h => h + ' мм').join(' + ')}` :
                     language === 'kz' ? `${panelLayout.length} қатар панель: ${panelLayout.map(h => h + ' мм').join(' + ')}` :
                     `${panelLayout.length} rows of panels: ${panelLayout.map(h => h + 'mm').join(' + ')}`}
                  </p>
                )}
              </div>
            </div>

            {/* Image Format (only in scratch mode) */}
            {generationMode === 'scratch' && (
            <div>
              <h3 className="text-white/60 font-medium text-sm mb-3">{language === 'en' ? 'Image format' : language === 'kz' ? 'Кескін форматы' : 'Формат изображения'}</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  { key: 'landscape', label: language === 'en' ? 'Landscape' : 'Альбомный', icon: <svg className="w-5 h-3" viewBox="0 0 20 12"><rect x="0.5" y="0.5" width="19" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1"/></svg> },
                  { key: 'portrait', label: language === 'en' ? 'Portrait (A4)' : 'Книжный (А4)', icon: <svg className="w-3 h-5" viewBox="0 0 12 20"><rect x="0.5" y="0.5" width="11" height="19" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1"/></svg> },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setImageFormat(f.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border-2 ${
                      imageFormat === f.key ? 'border-[#8b4444] bg-[#8b4444]/20 text-white' : 'border-white/10 text-white/50 hover:border-white/30'
                    }`}
                    data-testid={`format-${f.key}`}
                  >
                    {f.icon}
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Additional comment */}
            <div>
              <h3 className="text-white/60 font-medium text-sm mb-3">{language === 'en' ? 'Additional details (optional)' : language === 'kz' ? 'Қосымша сипаттама (міндетті емес)' : 'Дополнительный комментарий (необязательно)'}</h3>
              <textarea
                value={userComment}
                onChange={e => setUserComment(e.target.value)}
                placeholder={language === 'en' ? 'E.g.: light wood floor, white stretch ceiling, minimalist furniture...' : 'Напр.: светлый деревянный пол, белый натяжной потолок, минималистичная мебель...'}
                rows={2}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#8b4444] resize-none"
                data-testid="user-comment"
              />
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={!selectedPanel || (generationMode === 'scratch' && !selectedRoom) || (generationMode === 'photo' && !roomPhotoFile) || loading || cooldown > 0}
              className={`w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-3 ${
                !selectedPanel || (generationMode === 'scratch' && !selectedRoom) || (generationMode === 'photo' && !roomPhotoFile) || loading || cooldown > 0
                  ? 'bg-white/10 cursor-not-allowed text-white/30'
                  : 'bg-[#8b4444] hover:bg-[#7a3838] shadow-lg hover:shadow-xl'
              }`}
              data-testid="generate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.generating}
                </>
              ) : cooldown > 0 ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {language === 'ru' ? `Подождите ${cooldown} сек...` : `Wait ${cooldown}s...`}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {t.generate}
                </>
              )}
            </button>
          </div>

          {/* Right: Result */}
          <div className="min-w-0">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <span className="w-7 h-7 bg-[#8b4444] rounded-lg flex items-center justify-center text-white text-sm font-bold">3</span>
              {t.step3}
            </h3>
            <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden min-h-[400px] flex items-center justify-center" data-testid="result-area">
              {loading ? (
                <div className="text-center p-8" data-testid="constructor-loading">
                  <Loader2 className="w-12 h-12 text-[#8b4444] animate-spin mx-auto mb-4" />
                  <p className="text-white/60">{t.generating}</p>
                  <p className="text-white/40 text-sm mt-2" data-testid="constructor-elapsed">
                    {language === 'ru' ? `Прошло ${elapsed}с` : language === 'kz' ? `Өтті ${elapsed} сек` : `Elapsed ${elapsed}s`}
                  </p>
                </div>
              ) : error ? (
                <div className="text-center p-8" data-testid="constructor-error-banner">
                  <p className="text-red-400 mb-3">{error}</p>
                  {attemptCount >= 3 && (
                    <p className="text-amber-300/90 text-sm mb-4 max-w-md mx-auto leading-relaxed" data-testid="constructor-retry-hint">
                      {language === 'ru'
                        ? `Сделано ${attemptCount} попыток. Если не получается — попробуйте другой тип помещения или сменить цвет панели, на сложных комбинациях AI чаще не справляется.`
                        : `${attemptCount} attempts so far. If it keeps failing, try a different room type or a simpler panel color — complex combinations fail more often.`}
                    </p>
                  )}
                  <button onClick={handleGenerate} disabled={cooldown > 0} className="px-6 py-2 bg-[#8b4444] text-white rounded-xl hover:bg-[#7a3838] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="constructor-retry-btn">
                    <RotateCw className="w-4 h-4 inline mr-2" />
                    {cooldown > 0 ? `${t.regenerate} (${cooldown}s)` : t.regenerate}
                  </button>
                </div>
              ) : generatedImage ? (
                <div className="w-full">
                  <img src={generatedImage} alt="AI Generated Interior" className="w-full h-auto" data-testid="generated-image" />
                  <div className="p-4 flex gap-3">
                    <button
                      onClick={handleDownload}
                      className="flex-1 py-3 bg-[#F5F5F0] text-[#061d36] rounded-xl font-semibold hover:bg-white transition-colors flex items-center justify-center gap-2"
                      data-testid="download-btn"
                    >
                      <Download className="w-4 h-4" />
                      {t.download}
                    </button>
                    <button
                      onClick={handleGenerate}
                      className="flex-1 py-3 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
                      data-testid="regenerate-btn"
                    >
                      <RotateCw className="w-4 h-4" />
                      {t.regenerate}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center p-8">
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-white/20" />
                  </div>
                  <p className="text-white/30 text-sm">{t.noResult}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteriorConstructor;
